/**
 * MemorialDay.js — Memorial Day Sale 2026 landing page.
 *
 * Phase 1 of the Promotional Funnel System. Hardcoded for now; will become
 * the first row of a generic `funnels` collection in Phase 2.
 *
 * URL: /memorial
 * CTA routes the customer into the existing Instant Estimate funnel with
 * `?campaign=memorial-day-2026`, which triggers tier-discount logic and the
 * lead-source tagging in InstantEstimate.js.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";

// Memorial Day 2026 — Monday, May 25, 2026 at 11:59 PM PST
// (PST = UTC-7 in May / PDT)
const SALE_END_PST_ISO = "2026-05-26T06:59:59Z"; // 11:59 PM PDT = 06:59 UTC next day

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  const target = new Date(targetIso).getTime();
  const diff = Math.max(0, target - now);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds, expired: diff === 0 };
}

const Tier = ({ headline, savings, sub, highlight, badge }) => (
  <div
    data-testid={`tier-${headline.replace(/\s+/g, "-").toLowerCase()}`}
    className={`relative rounded-2xl p-6 border-2 ${
      highlight
        ? "border-[#207024] bg-gradient-to-br from-green-50 to-white shadow-xl scale-[1.02]"
        : "border-zinc-200 bg-white"
    }`}
  >
    {badge && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-zinc-900 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
        {badge}
      </div>
    )}
    <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
      {headline}
    </div>
    <div
      className={`mt-2 font-bold leading-tight ${
        highlight ? "text-[#207024] text-2xl" : "text-zinc-900 text-2xl"
      }`}
    >
      {savings}
    </div>
    <p className="mt-3 text-sm text-zinc-600">{sub}</p>
  </div>
);

export default function MemorialDay() {
  const navigate = useNavigate();
  const { days, hours, minutes, seconds, expired } = useCountdown(SALE_END_PST_ISO);

  const startQuote = () => {
    // Route into the existing Instant Estimate funnel with the campaign flag
    // so InstantEstimate.js applies the tier discount and tags the lead.
    navigate("/instant-estimate?campaign=memorial-day-2026");
  };

  const formCta = expired ? "Get My Instant Quote →" : "Check If My Home Qualifies →";

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Sticky banner */}
      <div className="bg-[#0a1f44] text-white text-center text-xs sm:text-sm font-semibold py-2 px-3">
        🇺🇸 MEMORIAL DAY SALE — Save up to $426 on whole-home screen replacements
      </div>

      {/* Hero */}
      <section className="px-5 sm:px-8 pt-8 pb-6 max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-[#b22234] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide">
          🇺🇸 Memorial Day Sale
        </div>
        <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold text-zinc-900 leading-tight">
          Replace Screens For Your Entire Home & Unlock Huge Savings
        </h1>
        <p className="mt-3 text-zinc-700 text-base sm:text-lg">
          The more screens you replace, the bigger your savings. For a limited time:
        </p>

        <ul className="mt-5 space-y-2.5 text-zinc-800">
          <li className="flex gap-2">
            <span className="text-[#207024] font-bold shrink-0">✓</span>
            <span><strong>10+ New Window Screens</strong> → $100 OFF</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#207024] font-bold shrink-0">✓</span>
            <span><strong>15+ New Window Screens</strong> → $200 OFF</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#207024] font-bold shrink-0">✓</span>
            <span><strong>20+ New Window Screens</strong> → FREE Sliding Screen Door ($426 Value)</span>
          </li>
        </ul>

        {/* Primary CTA */}
        <button
          onClick={startQuote}
          data-testid="memorial-hero-cta"
          className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#207024] hover:bg-green-700 text-white font-bold text-lg px-7 py-4 rounded-xl shadow-lg transition-all"
        >
          {formCta} <ArrowRight className="w-5 h-5" />
        </button>

        {/* Supporting trust line */}
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-600">
          <span className="inline-flex items-center gap-1 font-semibold">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            190+ 5-Star Reviews
          </span>
          <span className="text-zinc-400">•</span>
          <span>Mobile Service</span>
          <span className="text-zinc-400">•</span>
          <span>We Come To You</span>
          <span className="text-zinc-400">•</span>
          <span>Free Pickup &amp; Delivery</span>
        </div>
      </section>

      {/* Countdown */}
      {!expired && (
        <section className="px-5 sm:px-8 max-w-2xl mx-auto" data-testid="memorial-countdown">
          <div className="rounded-2xl bg-[#0a1f44] text-white p-5 text-center">
            <div className="text-xs sm:text-sm font-semibold uppercase tracking-wider opacity-80">
              Memorial Day Sale Ends In
            </div>
            <div className="mt-3 flex justify-center gap-2 sm:gap-4">
              {[
                { value: days, label: "Days" },
                { value: hours, label: "Hours" },
                { value: minutes, label: "Min" },
                { value: seconds, label: "Sec" },
              ].map((u) => (
                <div key={u.label} className="bg-white/10 rounded-xl px-3 py-2 min-w-[64px]">
                  <div className="text-2xl sm:text-3xl font-extrabold tabular-nums">
                    {String(u.value).padStart(2, "0")}
                  </div>
                  <div className="text-[10px] sm:text-xs uppercase opacity-70 mt-0.5">
                    {u.label}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs sm:text-sm opacity-80">
              Ends Memorial Day at Midnight
            </div>
          </div>
        </section>
      )}

      {/* Tier cards */}
      <section className="px-5 sm:px-8 mt-8 max-w-3xl mx-auto" data-testid="memorial-tiers">
        <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 text-center">
          Pick Your Tier — More Screens, Bigger Savings
        </h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          <Tier
            headline="10+ Screens"
            savings="Save $100"
            sub="Perfect for smaller whole-home refreshes"
          />
          <Tier
            headline="15+ Screens"
            savings="Save $200"
            sub="Most popular whole-home option"
            badge="Most Popular"
          />
          <Tier
            headline="20+ Screens"
            savings="FREE Sliding Screen Door"
            sub="Best value for larger homes — $426 value included"
            highlight
            badge="Best Value"
          />
        </div>
        <p className="text-center mt-3 text-xs text-zinc-500">
          Applies to new window screens only. Highest tier earned applies — no stacking.
        </p>
      </section>

      {/* Second CTA */}
      <section className="px-5 sm:px-8 my-10 max-w-2xl mx-auto text-center">
        <button
          onClick={startQuote}
          data-testid="memorial-second-cta"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#207024] hover:bg-green-700 text-white font-bold text-lg px-7 py-4 rounded-xl shadow-lg transition-all"
        >
          {formCta} <ArrowRight className="w-5 h-5" />
        </button>
        <p className="mt-3 text-xs text-zinc-500">
          Spots fill quickly — book your time now.
        </p>
      </section>

      {/* Fine print */}
      <footer className="px-5 sm:px-8 pb-12 max-w-2xl mx-auto">
        <p className="text-[11px] text-zinc-400 leading-snug">
          Memorial Day Sale ends Monday, May 25, 2026 at 11:59 PM PST. Discount
          applies to new window screen orders only and is auto-calculated based
          on quantity at checkout. Only the highest qualifying tier applies —
          discounts and free items cannot be stacked. Free sliding screen door
          tier valued at $426 and includes installation. Subject to service
          area availability.
        </p>
      </footer>
    </div>
  );
}
